import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { Step0Procedure } from './Step0Procedure';
import { Step0Osteotomy } from './Step0Osteotomy';
import { Step0Vilex } from './Step0Vilex';
import { Step0Matortho } from './Step0Matortho';
// import { Step0CMF } from './Step0CMF';
import { Step0CaseInfo } from './Step0CaseInfo';

import Button from '../../../../shared/Button';
import Alert from '../../../../shared/Alert';

import { createCase } from '../../../../../actions/casesActions';
import { setNotification } from '../../../../../actions/notificationActions';

import { userRoles, caseConstants } from '../../../../../constants';

import {
  selectedProcedureFlows,
  procedureFlows,
  fileTypes,
  defaultLeadDays,
  cmfExtendedProcedureFlows,
  matorthoProcedureFlows,
} from '../../../../../constants/cases';

const { GLOBAL_ADMIN, COUNTRY_ADMIN } = userRoles;

const vilexChildProcedureFlows = [
  procedureFlows.VILEX_H2,
  procedureFlows.VILEX_H3,
];

const matorthoChildProcedureFlows = [
  procedureFlows.MATORTHO_KNEE,
  procedureFlows.MATORTHO_HIP,
];

const getProcedureFlow = (flow) => {
  switch (flow) {
    case procedureFlows.HTO:
      return 'H';
    case procedureFlows.HTO_LATERAL:
      return 'HL';
    case procedureFlows.DFO_Medial:
      return 'DM';
    case procedureFlows.DFO_Lateral:
      return 'DL';
    case procedureFlows.ACL:
      return 'A';
    case procedureFlows.CMF:
      return 'C';
    case procedureFlows.CMF_EXTENDED:
      return 'CE';
    case procedureFlows.CUSTOM:
      return 'CM';
    case procedureFlows.DOUBLE_LEVEL_OSTEOTOMY:
      return 'DLO';
    case procedureFlows.ONCOL:
      return 'ON';
    case procedureFlows.CUSTOM_EXTENDED:
      return 'CE';
    case procedureFlows.MATORTHO:
    case procedureFlows.MATORTHO_KNEE:
    case procedureFlows.MATORTHO_HIP:
      return 'MT';
    case procedureFlows.VILEX:
      return 'V';
    default:
      return '';
  }
};

const Step0 = (props) => {
  const {
    uid,
    userRole,
    createCase,
    surgeons,
    hospitals,
    procedures,
    onClose,
    surgeonList,
    currentSurgeon,
    distributors,
    trainingMode,
    currentDistributor,
    selectedFlow,
    documents,
  } = props;

  const isAdmin = userRole === GLOBAL_ADMIN.name || userRole === COUNTRY_ADMIN.name;

  const [view, setView] = useState(0);
  const [selectedProcedureFlow, setSelectedProcedureFlow] = useState(null);
  const [selectedParentFlow, setSelectedParentFlow] = useState(null);
  const [surgeon, setSurgeon] = useState(currentSurgeon || null);
  const [distributor, setDistributor] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [procedure, setProcedure] = useState(null);
  const [date, setDate] = useState(isAdmin ? moment() : moment().add(defaultLeadDays, 'days'));
  const [dateTBC, setDateTBC] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [procedureSide, setProcedureSide] = useState('');
  const [psi, setPsi] = useState('yes');
  const [dateOfBirth, setDateOfBirth] = useState(moment('Jan 1, 1990'));
  const [caseNote, setCaseNote] = useState('');
  const [planningFileRequired, setPlanningFileRequired] = useState('no');
  const [consultationDate, setConsultationDate] = useState(moment().add(7, 'days'));

  const [reports, setReports] = useState([]);

  const [error, setError] = useState(false);
  const [checked, setChecked] = useState(false);

  const leadDays = useMemo(() => (
    procedure ? (procedure?.leadDays || 0) : defaultLeadDays
  ), [procedure]);
  const hasMatorthoChildProcedures = useMemo(
    () => procedures.some((item) => matorthoChildProcedureFlows.includes(item.flow)),
    [procedures]
  );
  const startDate = useMemo(() => (
    isAdmin ? moment() : moment().add(leadDays, 'days')
  ), [leadDays, isAdmin]);

  useEffect(() => {
    if (startDate) {
      setDate(startDate);
    }
  }, [startDate]);

  const documentList = useMemo(() => {
    const arr = procedure && procedure.documents ? procedure.documents : [];
    return arr
      .map((docId) => documents?.find((d) => d.id === docId))
      .filter((document) => !!document);
  }, [procedure, documents]);

  const checkMissingFields = () => {
    return (
      !distributor ||
      !surgeon ||
      !hospital ||
      !procedure ||
      !firstName ||
      !lastName ||
      !gender ||
      !dateOfBirth ||
      (!procedureSide && ![selectedProcedureFlows.CMF, selectedProcedureFlows.CMF_EXTENDED].includes(selectedFlow))
    );
  };

  const generateId = (flow) => {
    const lastNameCode = lastName.substr(0, 3);
    const firstNameCode = firstName.substr(0, 1);
    const surgeonCode = `${surgeon.firstName[0]}${surgeon.lastName[0]}`;
    const dateCode = dateOfBirth.format('DDMMYY');
    const procedureFlow = getProcedureFlow(flow);
    const procedureCode = procedureSide[0] || '0';

    return `${lastNameCode}${firstNameCode}${surgeonCode}${dateCode}${procedureFlow}${procedureCode}`.toUpperCase();
  };

  useEffect(() => {
    const startDate = isAdmin || [selectedProcedureFlows.ACL, selectedProcedureFlows.CMF, selectedProcedureFlows.CMF_EXTENDED, selectedProcedureFlows.ONCOL].includes(selectedFlow) ? moment() : moment().add(leadDays, 'days');
    setDate(startDate);
  }, [leadDays, selectedFlow]);

  const filteredDistributors = useMemo(() => {
    return distributors?.filter((distributor) =>
      !!distributor?.assignedProcedures?.find((procedure) => {
        const _procedure = procedures.find((p) => p.id === procedure.id);
        if (vilexChildProcedureFlows.includes(selectedFlow)) {
          return _procedure?.flow === procedureFlows.VILEX || _procedure?.flow?.includes(procedureFlows.VILEX);
        }
        if (matorthoChildProcedureFlows.includes(selectedFlow)) {
          return _procedure?.flow === selectedFlow;
        }
        if (selectedFlow === selectedProcedureFlows.MATORTHO) {
          return matorthoProcedureFlows.includes(_procedure?.flow);
        }
        return _procedure?.flow === selectedFlow || _procedure?.flow?.includes(selectedFlow);
      })
    );
  }, [distributors, selectedFlow, procedures]);

  useEffect(() => {
    if (currentSurgeon && filteredDistributors?.length === 1) {
      setDistributor(filteredDistributors[0]);
    }
  }, [filteredDistributors]);

  const onUpload = (files) => {
    setReports([...reports, ...files.filter((file) => !reports.map((sheet) => sheet.name).includes(file.name))]);
  };

  const onRemove = (name) => {
    setReports(reports.filter((file) => file.name !== name));
  };

  const handleSubmit = () => {
    if (checkMissingFields()) {
      setError(true);

      return;
    }

    // const minDate = selectedFlow !== selectedProcedureFlows.ACL || selectedFlow !== selectedProcedureFlows.CMF ? moment().add(27, 'days') : null;
    const minDate = moment().add(leadDays - 1, 'days');

    if ((!isAdmin && date.isBefore(minDate) && psi === 'yes' && !dateTBC)) {
      return;
    }

    const now = moment().format();
    const formattedId = generateId(procedure.flow);
    const suffix = uuidv4()?.substr(0, 4)?.toUpperCase();
    const caseId = `${formattedId}_${suffix}`;
    const newActivity = { ...caseConstants.caseNotifications[0].success, date: now, userId: uid };

    const _surgeon = surgeonList.find((s) => s.userId === surgeon.uid && s.distributor === distributor.id);

    if (!_surgeon) {
      return;
    }

    const skipPSI = psi === 'no';

    const files = [];
    const newFiles = [];

    reports.forEach((file) => {
      files?.unshift({
        id: `${caseId}_${fileTypes.report}_${file.name}`,
        name: file.name,
        type: fileTypes.report,
        date: now,
        userId: uid,
      });
      newFiles?.push(file);
    });

    const stepData = {
      surgeon: _surgeon.id,
      distributorId: distributor.id,
      company: distributor?.company || '',
      country: distributor?.country || '',
      hospital: hospital.name,
      hospitalUniq: { id: hospital.id, name: hospital.name },
      procedure: procedure.id,
      date: date && !dateTBC ? date.format() : null,
      procedureSide,
      procedureFlow: vilexChildProcedureFlows.includes(procedure.flow)
        ? procedureFlows.VILEX
        : matorthoChildProcedureFlows.includes(procedure.flow)
          ? procedureFlows.MATORTHO
          : (procedure.flow || ''),
      patientInfo: {
        firstName,
        lastName,
        gender,
        dateOfBirth: dateOfBirth.format('ll'),
      },
      completionDates: { step0: now },
      completedBy: { step0: uid },
      activity: [newActivity],
      status: caseConstants.statuses.inProgress,
      trainingMode,
      skipPSI,
      files,
      caseNote,
      formattedId,
      planningFileRequired: planningFileRequired === 'yes',
    };

    if (!!stepData.planningFileRequired && consultationDate) {
      stepData.consultationDate = consultationDate.format();
    }

    createCase(uid, caseId, stepData, newFiles).then(() => onClose(true, caseId));
  };

  const renderView = (view) => {
    switch (view) {
      case 0:
        return (
          <Step0Procedure
            selected={selectedProcedureFlow}
            onChange={(flow) => {
              setSelectedProcedureFlow(flow);
              if ([selectedProcedureFlows.Osteotomy, selectedProcedureFlows.VILEX, selectedProcedureFlows.MATORTHO].includes(flow)) {
                setSelectedParentFlow(flow);
              }
            }}
            procedures={procedures}
          />
        );
      case 1:
        if ([selectedProcedureFlows.VILEX, selectedProcedureFlows.VILEX_H2, selectedProcedureFlows.VILEX_H3].includes(selectedParentFlow)) {
          return (
            <Step0Vilex
              procedures={procedures}
              selected={selectedProcedureFlow}
              onChange={setSelectedProcedureFlow}
            />
          );
        }
        if (selectedParentFlow === selectedProcedureFlows.MATORTHO) {
          return (
            <Step0Matortho
              procedures={procedures}
              selected={selectedProcedureFlow}
              onChange={setSelectedProcedureFlow}
            />
          );
        }
        return (
          <Step0Osteotomy
            procedures={procedures}
            selected={selectedProcedureFlow}
            onChange={setSelectedProcedureFlow}
          />
        );
      case 2:
        return (
          <Step0CaseInfo
            distributor={distributor}
            distributors={filteredDistributors}
            setDistributor={setDistributor}
            selectedFlow={selectedFlow}
            selectedParentFlow={selectedParentFlow}
            currentSurgeon={currentSurgeon}
            surgeons={surgeons}
            surgeon={surgeon}
            setSurgeon={setSurgeon}
            surgeonList={surgeonList}
            procedures={procedures}
            procedure={procedure}
            setProcedure={setProcedure}
            hospitals={hospitals}
            hospital={hospital}
            setHospital={setHospital}
            documentList={documentList}
            date={date}
            setDate={setDate}
            isAdmin={isAdmin}
            psi={psi}
            setPsi={setPsi}
            startDate={startDate}
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            gender={gender}
            setGender={setGender}
            dateOfBirth={dateOfBirth}
            setDateOfBirth={setDateOfBirth}
            procedureSide={procedureSide}
            setProcedureSide={setProcedureSide}
            checked={checked}
            setChecked={setChecked}
            reports={reports}
            caseNote={caseNote}
            setCaseNote={setCaseNote}
            onUpload={onUpload}
            onRemove={onRemove}
            dateTBC={dateTBC}
            setDateTBC={setDateTBC}
            leadDays={leadDays}
            planningFileRequired={planningFileRequired}
            setPlanningFileRequired={setPlanningFileRequired}
            consultationDate={consultationDate}
            setConsultationDate={setConsultationDate}
          />
        );
      default:
        return (
          <Step0Procedure
            procedures={procedures}
            selected={selectedProcedureFlow}
            onChange={setSelectedProcedureFlow}
          />
        );
    }
  };

  const handleContinue = () => {
    if (
      selectedFlow === selectedProcedureFlows.Osteotomy ||
      selectedFlow === selectedProcedureFlows.VILEX ||
      (selectedFlow === selectedProcedureFlows.MATORTHO && hasMatorthoChildProcedures)
    ) {
      setView(view + 1);
      // if (selectedFlow === selectedProcedureFlows.Osteotomy) {
      //   setSelectedProcedureFlow(null);
      // }
      // if (selectedFlow === selectedProcedureFlows.CMF_GENERAL) {
      //   setSelectedProcedureFlow(selectedProcedureFlows.CMF);
      // }
    } else {
      setView(2);
    }
    setSelectedProcedureFlow(null);
  };

  const handleBack = () => {
    if ([selectedProcedureFlows.HTO, selectedProcedureFlows.DFO].includes(selectedFlow)) {
      setView(view - 1);
    } else {
      setView(0);
    }

    setSurgeon(currentSurgeon || null);
    setDistributor(currentDistributor || null);
    setSelectedProcedureFlow(null);
    setHospital(null);
    setProcedure(null);
    setFirstName('');
    setLastName('');
  };

  const isDisabled = () => {
    if ([procedureFlows.CMF, ...cmfExtendedProcedureFlows].includes(selectedFlow)) {
      return (!!documentList.length && !checked) || (!caseNote && !reports.length);
    } else {
      return !!documentList.length && !checked;
    }
  };

  return (
    <div className="case-step-container">
      <div className="case-step-content">{renderView(view)}</div>
      <div className="case-modal-actions">
        <div className="secondary pointer" onClick={onClose}>
          Close
        </div>

        <div className="step-0-buttons">
          {view !== 0 && <Button text="Back" type="secondary" color="primary" onClick={handleBack} />}

          {view === 2 ? (
            <Button
              text="Add Case"
              type="submit"
              color="primary"
              onClick={handleSubmit}
              disabled={isDisabled()}
            />
          ) : (
            <Button text="Continue" color="primary" disabled={!selectedProcedureFlow} onClick={handleContinue} />
          )}
        </div>
      </div>
      <Alert variant="warning" message="All fields are required" open={error} onClose={() => setError(false)} />
    </div>
  );
};

Step0.propTypes = {
  onClose: PropTypes.func,
  uid: PropTypes.string,
  userRole: PropTypes.string,
  surgeons: PropTypes.array,
  hospitals: PropTypes.array,
  procedures: PropTypes.array,
  createCase: PropTypes.func,
};

const mapStateToProps = (state) => {
  const currentUser = state.user.currentUser;
  const uid = currentUser.uid;
  const userRole = currentUser.role;
  const administrationCountries = currentUser?.administrationCountries || [];
  const hospitals = state.hospitals.list;
  const surgeonList = state.surgeons.list?.filter((s) => !s.disabled);
  const distributors = state.distributors.list;
  const procedures = state.procedures.list;
  const isSurgeonUser = userRole === userRoles.SURGEON.name;

  let surgeons = state.users.list.filter((user) => user.role === userRoles.SURGEON.name);
  const currentSurgeonAssignments = surgeonList.filter((item) => item.userId === uid);
  const currentUserDistributorIds = currentUser?.distributors?.filter(Boolean) || [];
  const assignedDistributorIds = isSurgeonUser
    ? (
      currentUserDistributorIds.length
        ? currentUserDistributorIds
        : currentSurgeonAssignments.map((assignment) => assignment.distributor).filter(Boolean)
    )
    : [];
  const surgeon = isSurgeonUser
    ? currentUser
    : surgeons.find((user) => user.uid === uid);

  const currentDistributor = assignedDistributorIds.length === 1
    ? distributors.find((distributor) => distributor.id === assignedDistributorIds[0])
    : null;

  let distributorsList =
    isSurgeonUser
      ? distributors.filter((item) => {
        return assignedDistributorIds.includes(item.id);
      })
      : distributors;

  let hospitalList =
    isSurgeonUser
      ? hospitals.filter((item) => {
          return currentSurgeonAssignments.some((assignment) => assignment.hospitals.includes(item.id));
        })
      : hospitals;

  if (userRole === userRoles.DISTRIBUTOR_ADMIN.name) {
    distributorsList = distributorsList?.filter((distributor) => currentUserDistributorIds.includes(distributor.id));
  }

  if (userRole === userRoles.SALES_REP.name) {
    const filteredSurgeons = surgeonList.filter((item) => item.salesRep.includes(uid));
    surgeons = state.users.list.filter((user) => filteredSurgeons?.map((item) => item.userId)?.includes(user.uid));
    distributorsList = distributorsList?.filter((distributor) => !!filteredSurgeons.find((surgeon) => surgeon.distributor === distributor.id))
  }

  if (userRole === userRoles.PRACTICE_MANAGER.name) {
    const filteredSurgeons = surgeonList.filter((item) => item.manager.includes(uid));
    surgeons = state.users.list.filter((user) => filteredSurgeons?.map((item) => item.userId)?.includes(user.uid));
    distributorsList = distributorsList?.filter((distributor) => !!filteredSurgeons.find((surgeon) => surgeon.distributor === distributor.id))
  }

  if (userRole === userRoles.REGISTRAR.name) {
    const filteredSurgeons = surgeonList.filter((item) => item?.registrar?.includes(uid));
    surgeons = state.users.list.filter((user) => filteredSurgeons?.map((item) => item.userId)?.includes(user.uid));
    distributorsList = distributorsList?.filter((distributor) => !!filteredSurgeons.find((surgeon) => surgeon.distributor === distributor.id))
  }

  if (userRole === userRoles.COUNTRY_ADMIN.name) {
    hospitalList = hospitalList?.filter((hospital) => administrationCountries.includes(hospital.country));
  }

  const proceduresList =
    isSurgeonUser || [userRoles.SALES_REP.name, userRoles.PRACTICE_MANAGER.name, userRoles.REGISTRAR.name, userRoles.COUNTRY_ADMIN.name]?.includes(userRole)
      ? procedures.filter((item) => {
          return distributorsList.some((d) => d.assignedProcedures.map((p) => p.id).includes(item.id));
        })
      : procedures;

  return {
    uid,
    userRole,
    surgeons,
    distributors: distributorsList,
    currentDistributor,
    hospitals: hospitalList,
    procedures: proceduresList,
    surgeonList: state.surgeons.list?.filter((s) => !s.disabled),
    currentSurgeon: surgeon,
    trainingMode: state.sidebar.trainingMode,
    documents: state.documents.list,
    selectedFlow: state.cases.selectedFlow,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    createCase: (uid, caseId, stepData, files) => dispatch(createCase(uid, caseId, stepData, files)),
    setNotification: (notification) => dispatch(setNotification(notification)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Step0);
